// useAuth.js -- owns the client auth session. Single job: resolve and expose the signed-in user (publishing
// its id to authSubject before each setUser, #1461), its resolved/auth-screen flags, and the sign-in actions.
import { useState, useEffect, useRef } from "react";
import * as Sentry from "../lib/errorReporting";
import { supabase } from "../lib/supabase";
import { BUILD_TYPE } from "../lib/constants";
import { isNative, isIOS, signInWithGoogleNative, signInWithAppleNative, reauthorizeWithAppleNative, isAuthCancellation, setupNativeAuthListener } from "../lib/nativeAuth";
import { getOrigin } from "../lib/platform";
import { identifyUser, resetUser, holdSignIn } from "../lib/analytics";
import { publishAuthSubject } from "../lib/authSubject";

export function useAuth() {
  const [user, setUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  // #1306: false until the initial session check finishes. Screen tracking waits on it,
  // because before getSession() resolves user is null AND showAuth is false, which reads
  // as "signed out, auth screen dismissed".
  const [authResolved, setAuthResolved] = useState(false);
  // PostHog (spec §4 steps 4-5). prevUserIdRef drives identify/reset (CC-IMPL-021: a
  // TOKEN_REFRESHED re-sets the same user and must not re-identify). lastSignedInIdRef
  // dedupes sign_in_completed: auth-js re-emits SIGNED_IN for a stored session on every
  // foreground, after INITIAL_SESSION, which seeds it.
  const prevUserIdRef = useRef(null);
  const lastSignedInIdRef = useRef(null);

  useEffect(() => {
    // Every setUser is preceded by publishAuthSubject: async onboarding-gate writers read the subject
    // synchronously, before React renders the new user (#1461 P1-D).
    if (!supabase) { publishAuthSubject(null); setUser(null); setAuthResolved(true); return; }
    let cancelled = false;
    let removeUrlListener = null;
    const init = async () => {
      try {
        // On native, await launch URL resolution before querying session —
        // prevents race where getSession() returns null before a cold-start
        // OAuth deep-link has had a chance to hydrate the session.
        if (isNative()) removeUrlListener = await setupNativeAuthListener();
        if (cancelled) return;
        // An expired session that cannot refresh resolves { session: null, error }: that is a
        // signed-out result (auth screen), not a failed check (Codex #1308).
        const { data: { session } } = await supabase.auth.getSession();
        if (cancelled) return;
        if (session?.user) { publishAuthSubject(session.user.id); setUser(session.user); }
        else { publishAuthSubject(null); setUser(null); setShowAuth(true); }
        setAuthResolved(true);
      } catch (initErr) {
        // Recoverable, so a warning (CC-IMPL-037): authResolved stays false (fail closed: no
        // anonymous screen_viewed) until onAuthStateChange delivers a user -- a sign-in, or
        // auth-js re-emitting SIGNED_IN for a stored session on the next foreground.
        if (!cancelled) {
          Sentry.captureMessage('auth-session-check-failed', {
            level: 'warning',
            tags: { function: 'useAuth.init', failureStage: 'session-check' },
            extra: { errorName: initErr?.name },
          });
        }
      }
    };
    init();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') {
        lastSignedInIdRef.current = session?.user?.id ?? null;
      } else if (event === 'SIGNED_IN' && session?.user?.id && session.user.id !== lastSignedInIdRef.current) {
        holdSignIn(session.user.id); // sign_in_completed, captured by the onboarding gate at `app` (#1438 F1)
        lastSignedInIdRef.current = session.user.id;
      } else if (event === 'SIGNED_OUT') {
        lastSignedInIdRef.current = null;
      }
      const u = session?.user ?? null;
      publishAuthSubject(u?.id);
      setUser(u);
      // A known user resolves auth (recovers a failed init). A null session never does:
      // INITIAL_SESSION(null) can fire before a native cold-start deep link hydrates.
      if (u) setAuthResolved(true);
      if (!u && BUILD_TYPE === 'Beta') setShowAuth(true);
    });
    return () => { cancelled = true; subscription.unsubscribe(); removeUrlListener?.(); };
  }, []);

  // Identity is the Supabase user id only (P3); signed-out users stay anonymous.
  useEffect(() => {
    const id = user?.id ?? null;
    if (id !== prevUserIdRef.current) {
      if (id) identifyUser(id);
      else resetUser();
    }
    prevUserIdRef.current = id;
  }, [user]);

  const signInWithGoogle = async () => {
    if (!supabase) return;
    try {
      if (isNative()) {
        await signInWithGoogleNative();
      } else {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: getOrigin(),
            queryParams: { access_type: 'offline', prompt: 'select_account' },
            skipBrowserRedirect: false,
          },
        });
        if (error) throw error;
      }
    } catch (signInErr) {
      Sentry.captureException(signInErr, {
        tags: { function: 'signInWithGoogle', failureStage: 'oauth-signin' },
        extra: { isNative: !!isNative() },
      });
      return { ok: false, error: signInErr };
    }
  };

  // Native Sign in with Apple (#1002). iOS-only in V1. Owns cancellation classification
  // and telemetry: cancel -> captureMessage(warning), genuine failure -> captureException
  // (CC-IMPL-037). Returns an explicit `cancelled` flag AuthScreen consumes for copy.
  const signInWithApple = async () => {
    if (!supabase) return;
    try {
      if (isIOS()) {
        await signInWithAppleNative();
      } else {
        return { ok: false, error: new Error('apple-unsupported-platform'), cancelled: false };
      }
    } catch (e) {
      const cancelled = isAuthCancellation(e);
      if (cancelled) {
        Sentry.captureMessage('apple-signin-cancelled', {
          level: 'warning',
          tags: { function: 'signInWithApple', failureStage: 'authorization-cancelled' },
        });
      } else {
        Sentry.captureException(e, {
          tags: { function: 'signInWithApple', failureStage: 'idtoken-signin' },
        });
      }
      return { ok: false, error: e, cancelled };
    }
  };

  // Apple re-authorization for account deletion (#1079). Same cancellation classification
  // and severity split as signInWithApple (CC-IMPL-037); returns the code, never stores it.
  const canReauthorizeWithApple = isIOS();
  const reauthorizeWithApple = async () => {
    if (!isIOS()) {
      return { ok: false, error: new Error('apple-unsupported-platform'), cancelled: false };
    }
    try {
      const { authorizationCode } = await reauthorizeWithAppleNative();
      return { ok: true, authorizationCode, cancelled: false };
    } catch (e) {
      const cancelled = isAuthCancellation(e);
      if (cancelled) {
        Sentry.captureMessage('apple-reauth-cancelled', { level: 'warning', tags: { function: 'reauthorizeWithApple', failureStage: 'authorization-cancelled' } });
      } else {
        Sentry.captureException(e, { tags: { function: 'reauthorizeWithApple', failureStage: 'authorize' } });
      }
      return { ok: false, error: e, cancelled };
    }
  };

  const openAuth = () => setShowAuth(true);
  const closeAuth = () => setShowAuth(false);

  return { user, showAuth, authResolved, signInWithGoogle, signInWithApple, reauthorizeWithApple, canReauthorizeWithApple, openAuth, closeAuth };
}
